// src/components/charts/ChartFrame.tsx
//
// ★★★ WAVE 27 — the parts every chart on this product must have, in one place.
//
// ⚠ **THIS EXISTS SO THE RULES CANNOT BE FORGOTTEN ONE CHART AT A TIME.**
// `docs/design/ux-direction.md` §5.3 makes four demands of anything plotted here,
// and each is the kind that is easy to honour on the chart you are looking at and
// easy to skip on the fifth one:
//
//   * **rule 10** — every chart states its period basis. `subtitle` is REQUIRED.
//   * **rule 2** — a table view always exists, with every plotted value. `rows`
//     and `columns` are REQUIRED, so a chart cannot be written without one.
//   * **rule 1** — a legend for two or more series; none for one, because the
//     title names it.
//   * **rule 3** — text wears text tokens, never the series colour. The swatch
//     beside a label carries identity; the label itself is ordinary ink.
//
// A caller supplies the picture. This supplies everything that makes the picture
// admissible, and there is no prop that turns any of it off.
import { useId, useMemo, useState, type ReactNode } from 'react'
import { SortableTh, SortAnnouncement } from '../SortableTh'
import { useClientSort, type ClientSortColumns } from '../../lib/useClientSort'

export interface ChartLegendEntry {
  readonly label: string
  /** 1-8, a slot in the validated categorical palette. Never a hex value. */
  readonly slot: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
}

export interface ChartTableColumn {
  readonly key: string
  readonly header: string
  readonly numeric?: boolean
}

/**
 * ★★★ WAVE 27 (owner-directed) — EVERY HEADER IN A CHART'S TABLE VIEW SORTS.
 *
 * ⚠ **THESE ARE THE TABLES THAT GET MISSED, and that is the whole reason the rule
 * exists.** A chart's table view looks incidental — a fallback behind a toggle,
 * not "a real table" — so it is exactly the surface a builder judges unimportant
 * and a reader actually scans. The owner has had to ask for this more than once,
 * always on a table somebody thought was minor.
 *
 * ⚠ **A CLIENT SORT IS HONEST HERE, and that is not true everywhere.** The rule
 * is that a capped or paginated list must sort SERVER-side, because a client sort
 * reorders one page and lies about the rest. These rows are the chart's complete
 * plotted set — every point it drew, nothing paged, nothing clamped — so sorting
 * them in the browser reorders the whole population by construction.
 *
 * ⚠ `useClientSort` carries the rest of the rule: nulls last in BOTH directions,
 * a third click returning to the server's own order, and `aria-sort` on the active
 * header via `SortableTh`, whose header is a real button rather than a styled cell.
 */
function inferredColumns(
  columns: readonly ChartTableColumn[],
): ClientSortColumns<Readonly<Record<string, string | number>>, string> {
  const spec: Record<string, { type: 'text' | 'number'; valueOf: (row: Readonly<Record<string, string | number>>) => string | number | null }> = {}
  for (const column of columns) {
    spec[column.key] = {
      // ⚠ A NUMERIC column sorts numerically even though the cell renders as a
      // formatted string: "1,000" sorts before "9" as text, which is the single
      // most common defect a table sort ships with.
      type: column.numeric === true ? 'number' : 'text',
      valueOf: (row) => row[column.key] ?? null,
    }
  }
  return spec as ClientSortColumns<Readonly<Record<string, string | number>>, string>
}

export function ChartFrame({
  title,
  subtitle,
  legend,
  children,
  columns,
  rows,
  emptyMessage,
}: {
  readonly title: string
  /** REQUIRED — the period basis, in the SERVER's words. §5.3 rule 10. */
  readonly subtitle: string
  readonly legend?: readonly ChartLegendEntry[]
  readonly children: ReactNode
  /** REQUIRED — the table view's shape. §5.3 rule 2; there is no way to omit it. */
  readonly columns: readonly ChartTableColumn[]
  readonly rows: readonly Readonly<Record<string, string | number>>[]
  readonly emptyMessage: string
}) {
  const [showTable, setShowTable] = useState(false)
  const tableId = useId()
  const empty = rows.length === 0

  const sortColumns = useMemo(() => inferredColumns(columns), [columns])
  const { rows: ordered, sort, onSort, interacted } = useClientSort(rows, sortColumns)
  const columnLabels = useMemo(
    () => Object.fromEntries(columns.map((column) => [column.key, column.header])),
    [columns],
  )

  return (
    <figure className="pt-chart-figure">
      <figcaption>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg">{title}</h3>
            <p className="text-sm text-[var(--sgds-color-subtle)]">{subtitle}</p>
          </div>
          {!empty && (
            <button
              type="button"
              className="pt-linkish"
              aria-expanded={showTable}
              aria-controls={tableId}
              onClick={() => setShowTable((open) => !open)}
            >
              {showTable ? 'Hide the figures' : 'Show the figures'}
            </button>
          )}
        </div>
      </figcaption>

      {empty ? (
        <p className="mt-4 text-[var(--sgds-body-color-subtle)]">{emptyMessage}</p>
      ) : (
        <>
          {/*
            ⚠ Two or more series get a legend; ONE never does (§5.3 rule 1). Adding
            one anyway "for consistency" teaches a reader to skip legends on the
            charts that actually need them.
          */}
          {legend !== undefined && legend.length >= 2 && (
            <ul className="pt-chart-legend">
              {legend.map((entry) => (
                <li key={entry.label}>
                  <span className={`pt-swatch pt-series-${entry.slot}`} aria-hidden="true" />
                  {entry.label}
                </li>
              ))}
            </ul>
          )}

          {children}

          <div id={tableId} hidden={!showTable}>
            {/*
              ⚠ The live region that announces a new order, so a screen-reader user
              is told the table re-sorted rather than left to discover it.
            */}
            <SortAnnouncement
              sort={sort}
              interacted={interacted}
              busy={false}
              columnLabels={columnLabels}
              rowCount={ordered.length}
            />
            <div className="pt-table-wrap mt-4">
              <table className="pt-table">
                <caption className="sr-only">
                  {title}: every plotted value. {subtitle}
                </caption>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <SortableTh
                        key={column.key}
                        label={column.header}
                        sortKey={column.key}
                        sort={sort}
                        onSort={onSort}
                        {...(column.numeric === true ? { align: 'right' as const } : {})}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((row, index) => (
                    <tr key={String(row[columns[0]?.key ?? ''] ?? index)}>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={column.numeric === true ? 'pt-tabular text-right' : undefined}
                        >
                          {typeof row[column.key] === 'number'
                            ? (row[column.key] as number).toLocaleString('en-SG')
                            : String(row[column.key] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </figure>
  )
}
